/**
 * `Callback` — pagina pubblica di completamento del flusso OIDC.
 *
 * Design ref: "Auth flow OIDC": `/callback` riceve `code` + `state` dal provider
 * e completa lo scambio (`signinRedirectCallback`) via `handleCallback()`.
 *
 * Comportamento:
 *  - attende che l'inizializzazione dell'auth sia completata (`isLoading` a
 *    `false`): solo allora il `UserManager` esiste (o la config è fallita) e
 *    `handleCallback()` può essere invocato senza incorrere nella race
 *    "AuthProvider non inizializzato";
 *  - a inizializzazione completata esegue `handleCallback()` una sola volta;
 *  - al successo naviga alla home (`/`) con `replace` (la URL con `code` non
 *    resta in cronologia);
 *  - in caso di errore mostra un messaggio con un link per riprovare da `/login`.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Loading } from '../components/ui';

export default function Callback() {
  const { handleCallback, isLoading } = useAuth();
  const navigate = useNavigate();
  const startedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Non avviare lo scambio finché l'inizializzazione dell'auth è in corso:
    // il `UserManager` potrebbe non essere ancora pronto. L'effetto viene
    // rieseguito quando `isLoading` diventa `false` (dep sotto).
    if (isLoading) return;
    if (startedRef.current) return;
    startedRef.current = true;
    handleCallback()
      .then(() => {
        navigate('/', { replace: true });
      })
      .catch((err) => {
        console.error('Callback: completamento accesso non riuscito', err);
        setError(
          err instanceof Error ? err.message : 'Completamento accesso non riuscito.',
        );
      });
  }, [isLoading, handleCallback, navigate]);

  return (
    <section aria-labelledby="callback-title" className="auth-status">
      <img className="navbar__logo" src="/imgs/icon.svg" alt="" aria-hidden="true" />
      <h1 id="callback-title">Accesso</h1>
      {error === null ? (
        <Loading>Completamento accesso…</Loading>
      ) : (
        <div role="alert">
          <p>Non è stato possibile completare l'accesso.</p>
          <p className="auth-status__detail">{error}</p>
          <Link to="/login">← Torna alla pagina di accesso</Link>
        </div>
      )}
    </section>
  );
}
